import type {
  CallbackResult,
  IceServer,
  ChatStreamEvent,
  PageContext,
  PreChatFields,
  WidgetConversationSummary,
  WidgetHistory,
  WidgetPublicConfig,
  WidgetSession,
} from './types';

export class WidgetApiError extends Error {
  /** Per-field messages from a 422, e.g. { email: 'Required' }. */
  fieldErrors: Record<string, string> = {};

  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Client for the public widget API (/api/v1/widget). No cookies; a visitor token instead. */
export class WidgetApi {
  private readonly base: string;

  constructor(
    apiUrl: string,
    private readonly key: string,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {
    this.base = `${apiUrl.replace(/\/+$/, '')}/api/v1/widget`;
  }

  private async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await this.fetchImpl(`${this.base}${path}`, init);
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    if (!res.ok) throw new WidgetApiError(res.status, body?.error?.message ?? 'Request failed');
    return body as T;
  }

  config(): Promise<WidgetPublicConfig> {
    return this.json(`/config?key=${encodeURIComponent(this.key)}`);
  }

  session(visitorToken: string | null): Promise<WidgetSession> {
    return this.json('/sessions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ key: this.key, ...(visitorToken ? { visitorToken } : {}) }),
    });
  }

  /** The current chat, or one of the visitor's earlier chats by id. */
  history(token: string, conversationId?: string): Promise<WidgetHistory> {
    const q = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
    return this.json(`/messages${q}`, { headers: { authorization: `Bearer ${token}` } });
  }

  /** The visitor's chats, newest first, for "Recent chats". */
  conversations(token: string): Promise<WidgetConversationSummary[]> {
    return this.json('/conversations', { headers: { authorization: `Bearer ${token}` } });
  }

  /** Asks the team to call the visitor back. Field errors come back as `fieldErrors`. */
  async callback(token: string, input: { phone: string; email?: string }): Promise<CallbackResult> {
    const res = await this.fetchImpl(`${this.base}/callback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
    });
    const body = (await res.json().catch(() => null)) as
      | (CallbackResult & {
          error?: { message?: string; details?: { path: string; message: string }[] };
        })
      | null;
    if (res.ok && body) return { phone: body.phone, promise: body.promise };
    const err = new WidgetApiError(res.status, body?.error?.message ?? 'Request failed');
    err.fieldErrors = Object.fromEntries(
      (body?.error?.details ?? []).map((d) => [d.path, d.message]),
    );
    throw err;
  }

  /** Rings the team for a browser voice call. */
  startCall(
    token: string,
    socketId: string | null,
  ): Promise<{ callId: string; iceServers: IceServer[] }> {
    return this.json('/calls', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(socketId ? { socketId } : {}),
    });
  }

  /** Hangs up or stops ringing. `keepalive` lets it finish while the page is closing. */
  async endCall(token: string, callId: string): Promise<void> {
    await this.fetchImpl(`${this.base}/calls/${encodeURIComponent(callId)}/end`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      keepalive: true,
    });
  }

  /** Submits the pre-chat form. Field errors come back as `fieldErrors` (path → message). */
  /** The visitor opened the chat (the business may get a WhatsApp alert). Best-effort. */
  async opened(token: string, page: { url?: string; title?: string }): Promise<void> {
    await this.fetchImpl(`${this.base}/opened`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(page),
    });
  }

  /** "Continue on WhatsApp": a wa.me link that links this chat to the visitor's WhatsApp. */
  async whatsAppLink(token: string): Promise<string> {
    const res = await this.fetchImpl(`${this.base}/whatsapp-link`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new WidgetApiError(res.status, 'WhatsApp is not available right now');
    return ((await res.json()) as { url: string }).url;
  }

  async lead(token: string, fields: PreChatFields): Promise<void> {
    const res = await this.fetchImpl(`${this.base}/lead`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(fields),
    });
    if (res.ok) return;
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string; details?: { path: string; message: string }[] };
    } | null;
    const err = new WidgetApiError(
      res.status,
      body?.error?.message ?? 'Details could not be saved',
    );
    err.fieldErrors = Object.fromEntries(
      (body?.error?.details ?? []).map((d) => [d.path, d.message]),
    );
    throw err;
  }

  /** Rates the visitor's latest chat 1–5; sent again with a comment after a low rating. */
  async rate(token: string, rating: number, comment?: string): Promise<void> {
    const res = await this.fetchImpl(`${this.base}/csat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ rating, ...(comment ? { comment } : {}) }),
    });
    if (!res.ok) throw new WidgetApiError(res.status, 'Rating could not be saved');
  }

  /** 👍/👎 on an AI answer. */
  async feedback(token: string, messageId: string, rating: 'up' | 'down'): Promise<void> {
    const res = await this.fetchImpl(`${this.base}/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ messageId, rating }),
    });
    if (!res.ok) throw new WidgetApiError(res.status, 'Feedback could not be saved');
  }

  /** Sends a message and calls `onEvent` for each Server-Sent Event of the reply. */
  async send(
    token: string,
    text: string,
    pageContext: PageContext,
    onEvent: (e: ChatStreamEvent) => void,
    opts: { newConversation?: boolean; handoff?: boolean } = {},
  ) {
    const res = await this.fetchImpl(`${this.base}/messages`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({
        text,
        pageContext,
        ...(opts.newConversation ? { newConversation: true } : {}),
        ...(opts.handoff ? { handoff: true } : {}),
      }),
    });
    await this.readEvents(res, onEvent, 'Message could not be sent');
  }

  /** Uploads a recorded voice note; the reply streams back like a message. */
  async sendVoiceNote(
    token: string,
    audio: Blob,
    durationMs: number,
    onEvent: (e: ChatStreamEvent) => void,
    opts: { newConversation?: boolean } = {},
  ) {
    const form = new FormData();
    form.append('file', audio, 'voice-note');
    form.append('durationMs', String(Math.round(durationMs)));
    if (opts.newConversation) form.append('newConversation', 'true');
    const res = await this.fetchImpl(`${this.base}/voice-notes`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: form,
    });
    await this.readEvents(res, onEvent, 'Voice note could not be sent');
  }

  /** A voice note's recording, as a URL the page can play. */
  async voiceNoteUrl(token: string, messageId: string): Promise<string> {
    const res = await this.fetchImpl(`${this.base}/voice-notes/${encodeURIComponent(messageId)}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new WidgetApiError(res.status, 'Voice note unavailable');
    return URL.createObjectURL(await res.blob());
  }

  /** Reads a Server-Sent Events reply, calling `onEvent` for each event. */
  private async readEvents(res: Response, onEvent: (e: ChatStreamEvent) => void, failure: string) {
    if (!res.ok || !res.body) {
      const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new WidgetApiError(res.status, body?.error?.message ?? failure);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const parts = buffer.split('\n\n');
      buffer = done ? '' : parts.pop()!;
      for (const part of parts) {
        const data = part
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).trimStart())
          .join('\n');
        if (data) onEvent(JSON.parse(data) as ChatStreamEvent);
      }
      if (done) return;
    }
  }
}

export function currentPageContext(): PageContext {
  const utm: Record<string, string> = {};
  try {
    new URL(location.href).searchParams.forEach((v, k) => {
      if (k.startsWith('utm_')) utm[k.slice(4)] = v.slice(0, 200);
    });
  } catch {
    // Non-standard location; skip UTM.
  }
  return {
    url: location.href.slice(0, 2048),
    title: document.title.slice(0, 300),
    ...(document.referrer ? { referrer: document.referrer.slice(0, 2048) } : {}),
    ...(Object.keys(utm).length ? { utm } : {}),
  };
}
